import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import Layout from "../../components/Layout";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, ChevronLeft, ShieldCheck, Wallet, Landmark, Receipt } from "lucide-react";
import ReactCountryFlag from "react-country-flag";
import { useApp } from "../../context/AppContext";
import { scopedTransactionSelect, scopedTransactionUpdate } from '../../utils/secureQuery';
import { parseUuid } from '../../utils/inputValidation';

// --- Componente de Logo ---
const MethodLogo = ({ methodName, size = "large" }) => {
  const [hasError, setHasError] = useState(false);

  const formattedName = methodName?.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, '-') || 'default';
    
  const imagePath = `/logos/${formattedName}.png`;
  const sizeClasses = size === "large" ? "w-14 h-14" : "w-10 h-10";
  const iconSize = size === "large" ? "w-6 h-6" : "w-5 h-5";

  const getFallbackIcon = () => {
    const name = methodName?.toLowerCase() || '';
    if (name.includes("transferencia") || name.includes("banco") || name.includes("pichincha")) {
      return <Landmark className={`${iconSize} text-slate-400`} />;
    }
    return <Wallet className={`${iconSize} text-slate-400`} />;
  };

  if (hasError) {
    return (
      <div className={`${sizeClasses} shrink-0 rounded-full flex items-center justify-center shadow-sm border bg-slate-50 border-slate-200`}>
        {getFallbackIcon()}
      </div>
    );
  }

  return (
    <div className={`${sizeClasses} shrink-0 rounded-full flex items-center justify-center overflow-hidden bg-white shadow-sm border border-slate-200`}>
      <img src={imagePath} alt={`Logo`} className="w-full h-full object-cover p-1" onError={() => setHasError(true)} />
    </div>
  );
};

// Map de nombres amigables de métodos
const METHOD_NAMES = {
  pagomovil: "Pago móvil",
  bank_transfer: "Transferencia bancaria",
  western: "Western Union",
  zelle: "Zelle",
  bancolombia: "Bancolombia",
};

// Map de etiquetas de campos
const FIELD_LABELS = {
  holder_name: "Titular",
  document_type: "Tipo de documento",
  document_number: "N° de documento",
  account_number: "N° Cuenta / Teléfono",
  bank: "Banco",
  account_type: "Tipo de cuenta",
  platform: "Plataforma",
};

// Símbolos de moneda por país
const CURRENCY_BY_COUNTRY = {
  VE: "Bs",
  CO: "COP",
  PE: "S/.",
  US: "USD",
  PA: "USD",
  EC: "USD",
};

// Info de países
const COUNTRY_INFO = {
  VE: { name: "Venezuela" },
  CO: { name: "Colombia" },
  PE: { name: "Perú" },
  US: { name: "Estados Unidos" },
  PA: { name: "Panamá" },
  EC: { name: "Ecuador" },
};

// Formateador de moneda
const formatCurrency = (amount, countryCode) => {
  if (!amount && amount !== 0) return "";
  const symbol = CURRENCY_BY_COUNTRY[countryCode] || "$";
  const formattedNumber = Number(amount).toLocaleString("es-CO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol} ${formattedNumber}`;
};

export default function PaymentSummaryStep() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useApp();
  const { transactionId } = location.state || {};

  const [transaction, setTransaction] = useState(null);
  const [originMethod, setOriginMethod] = useState(null);
  const [destinationMethod, setDestinationMethod] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTransaction = async () => {
      setLoading(true);

      if (!transactionId || !user?.id) {
        setLoading(false);
        return;
      }

      const { data: trx, error: trxError } = await scopedTransactionSelect(
        supabase,
        transactionId,
        user.id
      ).single();

      if (trxError) {
        console.error("Error cargando transacción:", trxError);
        setLoading(false);
        return;
      }

      setTransaction(trx);

      if (trx.payment_method_origin_id) {
        const { data: origin } = await supabase
          .from("payment_methods_origin")
          .select("*")
          .eq("id", parseUuid(trx.payment_method_origin_id, 'Método origen'))
          .single();
        if (origin) setOriginMethod(origin);
      }

      if (trx.payment_method_destination_id) {
        const { data: dest } = await supabase
          .from("user_payment_methods")
          .select("*")
          .eq("id", parseUuid(trx.payment_method_destination_id, 'Método destino'))
          .eq("user_id", user.id)
          .single();
        if (dest) setDestinationMethod(dest);
      }

      setLoading(false);
    };

    fetchTransaction();
  }, [transactionId, user?.id]);

  const handleContinue = async () => {
    if (!transaction || !user?.id) return;

    const now = new Date().toISOString();
    const updates = {
      status: "en pago",
      current_step: "payment_destination",
    };

    if (!transaction.timer_started_at) {
      updates.timer_started_at = now;
      updates.timer_duration = 600; 
    }

    const { error } = await scopedTransactionUpdate(supabase, transaction.id, user.id, updates);

    if (!error) navigate("/send/step5", { state: { transactionId: transaction.id } });
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Layout showNavigation={false}>
        <div className="min-h-screen flex items-center justify-center bg-[#f4f7fb]">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            <p className="text-blue-900/70 font-black uppercase tracking-widest text-xs">Preparando resumen...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!transaction) {
    return (
      <Layout showNavigation={false}>
        <div className="min-h-screen flex items-center justify-center bg-[#f4f7fb] p-5">
          <div className="text-center bg-white p-6 rounded-[28px] shadow-sm border border-slate-100 max-w-sm w-full">
            <div className="w-14 h-14 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold">!</span>
            </div>
            <p className="text-xl font-black text-slate-800 tracking-tight">Sesión expirada</p>
            <p className="mt-2 text-slate-500 text-sm">No se pudo cargar el resumen. Por favor, inicia tu envío nuevamente.</p>
            <button onClick={() => navigate('/send/step1')} className="mt-6 w-full py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-black rounded-xl transition-colors uppercase tracking-widest text-xs">Volver al inicio</button>
          </div>
        </div>
      </Layout>
    );
  }

  const renderField = (label, value) => {
    if (!value && value !== 0) return null;
    return (
      <div className="flex justify-between items-start py-2.5 border-b border-slate-100 last:border-0 gap-4">
        <span className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-0.5 shrink-0">{label}</span>
        <span className="font-bold text-slate-800 text-right text-[13px] break-all">{value}</span>
      </div>
    );
  };

  const originInfo = COUNTRY_INFO[transaction.origin_country];
  const destInfo = COUNTRY_INFO[transaction.destination_country];

  return (
    <Layout showNavigation={false}>
      <div className="min-h-screen bg-[#f4f7fb] px-4 py-4 md:py-8 relative">
        <div className="mx-auto w-full max-w-md">
          
          {/* ENCABEZADO (Más compacto) */}
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-5">
            <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-600 rounded-full mb-2 shadow-sm">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight leading-tight">
              Verifica tu orden
            </h1>
            <p className="text-slate-500 text-xs font-medium mt-1">Revisa que todo esté correcto antes de pagar.</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="space-y-3">
            
            {/* ==============================================
                TARJETA 1: EL RECIBO DESGLOSADO
                ============================================== */}
            <div className="bg-white rounded-[24px] shadow-[0_5px_20px_rgba(0,0,0,0.02)] border border-slate-100 overflow-hidden relative">
              <div className="h-1.5 w-full bg-gradient-to-r from-blue-500 to-indigo-600"></div>
              
              <div className="p-4 md:p-5">
                
                {/* 1. Flujo de dinero (Reducido padding) */}
                <div className="flex flex-col md:flex-row items-center justify-between gap-3 md:gap-2 mb-4 bg-slate-50/50 p-3 rounded-[20px] border border-slate-100/50">
                  
                  {/* Origen */}
                  <div className="flex flex-col items-center md:items-start w-full md:w-auto text-center md:text-left">
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-0.5">Tú envías</span>
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <div className="rounded-sm overflow-hidden shadow-sm flex items-center justify-center w-[18px] h-[13px]">
                        <ReactCountryFlag countryCode={transaction.origin_country} svg style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <span className="font-bold text-slate-600 text-[13px]">{originInfo?.name}</span>
                    </div>
                    <span className="text-xl font-black text-blue-600 tracking-tighter leading-none">
                      {formatCurrency(transaction.amount, transaction.origin_country)}
                    </span>
                  </div>

                  {/* Flecha */}
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-white border border-slate-200 shadow-sm shrink-0 my-0.5 md:my-0">
                    <ArrowDown className="w-3.5 h-3.5 text-slate-400 md:hidden" />
                    <ArrowRight className="w-3.5 h-3.5 text-slate-400 hidden md:block" />
                  </div>

                  {/* Destino */}
                  <div className="flex flex-col items-center md:items-end w-full md:w-auto text-center md:text-right">
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-0.5">Destinatario recibe</span>
                    <div className="flex items-center gap-1.5 mb-0.5 justify-center md:justify-end">
                      <span className="font-bold text-slate-600 text-[13px]">{destInfo?.name}</span>
                      <div className="rounded-sm overflow-hidden shadow-sm flex items-center justify-center w-[18px] h-[13px]">
                        <ReactCountryFlag countryCode={transaction.destination_country} svg style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    </div>
                    <span className="text-xl font-black text-green-500 tracking-tighter leading-none">
                      {formatCurrency(transaction.received_amount, transaction.destination_country)}
                    </span>
                  </div>
                </div>

                {/* 2. Tasa de cambio */}
                <div className="flex justify-between items-center mb-4 px-1">
                  <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest flex items-center gap-1.5">
                    <Receipt className="w-3 h-3" /> Tasa aplicada
                  </span>
                  <span className="text-[11px] font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-md">{transaction.rate}</span>
                </div>

                {/* 3. Desglose Financiero */}
                <div className="space-y-2.5 pt-3 border-t border-dashed border-slate-200 px-1">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 font-medium text-[13px]">Monto del envío</span>
                    <span className="font-bold text-slate-800 text-[13px]">
                      {formatCurrency(transaction.amount, transaction.origin_country)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 font-medium text-[13px]">Comisión plataforma</span>
                    {Number(transaction.payment_fee) === 0 ? (
                      <span className="inline-flex items-center px-2 py-0.5 bg-emerald-100/80 text-emerald-700 text-[9px] font-black uppercase tracking-widest rounded border border-emerald-200/50">
                        Gratis
                      </span>
                    ) : (
                      <span className="font-bold text-slate-500 text-[13px]">
                        + {formatCurrency(transaction.payment_fee, transaction.origin_country)}
                      </span>
                    )}
                  </div>
                </div>

                {/* 4. TOTAL A PAGAR (CAJA PREMIUM SIMÉTRICA Y RESPONSIVA) */}
                <div className="mt-4 md:mt-5 relative overflow-hidden bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 rounded-[20px] p-4 md:p-5 shadow-[0_8px_20px_rgba(30,58,138,0.2)] border border-blue-700/50">
                  {/* Brillos decorativos balanceados */}
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-400/20 rounded-full blur-2xl pointer-events-none"></div>
                  <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-blue-500/20 rounded-full blur-2xl pointer-events-none"></div>
                  
                  <div className="relative z-10 flex flex-row items-center justify-between gap-2">
                    
                    {/* Lado Izquierdo: Textos */}
                    <div className="flex flex-col justify-center gap-1.5 shrink-0">
                      <span className="text-[10px] md:text-[11px] font-black uppercase tracking-widest text-blue-200">
                        Total a Pagar
                      </span>
                      <span className="text-[8px] md:text-[9px] font-medium text-blue-200/90 bg-blue-950/40 px-2 py-0.5 rounded-md inline-flex w-fit border border-blue-800/50 backdrop-blur-sm shadow-sm">
                        Comisión incluida
                      </span>
                    </div>

                    {/* Lado Derecho: Monto y Moneda alineados */}
                    <div className="flex items-baseline justify-end gap-1 md:gap-1.5 min-w-0">
                      <span className="text-[24px] sm:text-[28px] md:text-3xl font-black text-white tracking-tighter leading-none truncate">
                        {formatCurrency(transaction.total_to_pay, transaction.origin_country).split(' ')[1]}
                      </span>
                      <span className="text-[9px] md:text-[11px] font-bold text-blue-300 uppercase tracking-widest shrink-0">
                        {transaction.origin_currency || CURRENCY_BY_COUNTRY[transaction.origin_country]}
                      </span>
                    </div>

                  </div>
                </div>

              </div>
            </div>

            {/* ==============================================
                TARJETA 2: DATOS DEL DESTINATARIO
                ============================================== */}
            {destinationMethod && (
              <div className="bg-white rounded-[24px] p-4 md:p-5 shadow-sm border border-slate-100">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="bg-green-100 p-1.5 rounded-lg text-green-600 shrink-0"><Landmark className="w-4 h-4" /></div>
                  <h3 className="font-black text-slate-800 text-[13px] uppercase tracking-widest">A quién envías</h3>
                </div>

                <div className="flex items-center gap-3 mb-3 bg-slate-50 p-3 rounded-2xl border border-slate-100/80">
                  <MethodLogo methodName={METHOD_NAMES[destinationMethod.method] || destinationMethod.method} size="small" />
                  <div className="min-w-0 flex-1">
                    <p className="font-black text-slate-800 text-[13px] truncate">{METHOD_NAMES[destinationMethod.method] || destinationMethod.method}</p>
                    <p className="text-[10px] font-bold text-slate-500 uppercase truncate mt-0.5">{destinationMethod.data?.bank || 'Recepción directa'}</p>
                  </div>
                </div>

                <div className="bg-slate-50/50 rounded-xl px-3 border border-slate-50">
                  {destinationMethod.data && Object.entries(destinationMethod.data)
                    .filter(([_, v]) => v)
                    .map(([key, value]) => {
                      const label = FIELD_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
                      return <div key={key}>{renderField(label, value)}</div>;
                  })}
                  {destinationMethod.account_type && renderField("Tipo de cuenta", destinationMethod.account_type)}
                </div>
              </div>
            )}

            {/* ==============================================
                TARJETA 3: TU MÉTODO DE PAGO
                ============================================== */}
            {originMethod && (
              <div className="bg-white rounded-[24px] p-4 md:p-5 shadow-sm border border-slate-100">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="bg-blue-100 p-1.5 rounded-lg text-blue-600 shrink-0"><Wallet className="w-4 h-4" /></div>
                  <h3 className="font-black text-slate-800 text-[13px] uppercase tracking-widest">Cómo vas a pagar</h3>
                </div>

                <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-2xl border border-slate-100/80">
                  <MethodLogo methodName={METHOD_NAMES[originMethod.method] || originMethod.method} size="small" />
                  <div className="min-w-0 flex-1">
                    <p className="font-black text-slate-800 text-[13px] truncate">{METHOD_NAMES[originMethod.method] || originMethod.method}</p>
                    {originMethod.holder_name && <p className="text-[10px] font-bold text-slate-500 uppercase truncate mt-0.5">{originMethod.holder_name}</p>}
                    {(originMethod.document_type || originMethod.document_number) && (
                      <p className="text-[9px] font-mono text-slate-400 mt-1 truncate">
                        {`${originMethod.document_type || ""} ${originMethod.document_number || ""}`.trim()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

          </motion.div>

          {/* ESPACIADOR FINAL (Más pequeño) */}
          <div className="h-28 w-full"></div>

        </div>

        {/* ==============================================
            BOTONERA FIJA
            ============================================== */}
        <div className="fixed bottom-0 left-0 w-full bg-white/95 backdrop-blur-xl border-t border-slate-200/50 p-3.5 z-40 pb-safe shadow-[0_-5px_20px_rgba(0,0,0,0.03)]">
          <div className="max-w-md mx-auto flex gap-3">
            <button
              onClick={handleBack}
              className="flex items-center justify-center p-3.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-2xl transition-all active:scale-95 border border-slate-200/50"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={handleContinue}
              className="flex-1 py-3.5 px-6 bg-blue-600 hover:bg-blue-700 text-white font-black text-[14px] rounded-2xl transition-all active:scale-[0.98] shadow-[0_8px_20px_rgba(37,99,235,0.25)] flex items-center justify-center gap-2 uppercase tracking-widest"
            >
              Confirmar envío <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

      </div>
    </Layout>
  );
}

